import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AvatarUploadField } from '@/components/atoms/AvatarUploadField/AvatarUploadField';
import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useLiveChat } from '@/hooks/useLiveChat';
import { LIVECHAT_BOT_NAME_MAX_LENGTH } from '@/shared/constants/livechat';
import { LiveChatBotProfile, LiveChatIntegration } from '@/shared/types/integration';
import { trimOnBlur } from '@/util/validation/helpers';

import { CardBaseHeader } from '../Components/CardBase';

interface Props {
  integration: LiveChatIntegration;
  canWrite: boolean;
}

const FORM_ID = 'livechat-profile-form';

const ProfileCard = ({ integration, canWrite }: Props) => {
  const { t } = useTranslation();
  const { name, avatar, botStatus, saveProfile, saveProfileStatus } = useLiveChat(
    integration.desk_id,
    integration.integration_id
  );

  const form = useForm<LiveChatBotProfile>({ values: { name: name ?? '', avatar } });

  const {
    control,
    handleSubmit,
    formState: { isDirty, dirtyFields },
  } = form;

  // LiveChat's stored avatar may fail the server's URL check, so only a new upload is sent.
  const onSubmit = (values: LiveChatBotProfile) => {
    const profile = { name: values.name.trim() };
    saveProfile(dirtyFields.avatar ? { ...profile, avatar: values.avatar } : profile);
  };

  return (
    <div className="w-full">
      <CardBaseHeader
        title={t('integrations.livechat.profile.title')}
        titleVariant="secondary"
        status={integration.status}
        active={integration.active}
      />
      <Form {...form}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={saveProfileStatus === 'pending'}
            disabled={!isDirty || !canWrite}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            {botStatus === 'error' ? (
              <p role="alert" className="text-sm text-destructive">
                {t('integrations.livechat.load_error')}
              </p>
            ) : (
              <div className="space-y-4">
                <FormField
                  control={control}
                  name="avatar"
                  render={({ field }) => (
                    <AvatarUploadField
                      id="livechat-bot-avatar-upload"
                      name={name}
                      avatar={field.value}
                      onUpdate={(uploaded) => field.onChange(uploaded.avatar)}
                      readOnly={!canWrite}
                      label={t('integrations.livechat.profile.avatar')}
                      recommendedHint={t('profile.avatar_recommended')}
                      supportedHint={t('profile.avatar_supported')}
                    />
                  )}
                />
                <FormField
                  control={control}
                  name="name"
                  rules={{
                    validate: (value) => value.trim() !== '' || t('common.required_field'),
                    maxLength: {
                      value: LIVECHAT_BOT_NAME_MAX_LENGTH,
                      message: t('validation.max_length', { 0: LIVECHAT_BOT_NAME_MAX_LENGTH }),
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.name')}</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="off"
                          {...field}
                          onBlur={trimOnBlur(field)}
                          readOnly={!canWrite}
                          disabled={botStatus === 'pending'}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('integrations.livechat.profile.name_subtitle')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};

export default ProfileCard;
